// ─────────────────────────────────────────────
//  MobileAlertButtons — the two-button alert card for ONE room (mobile).
//
//  The prototype's alert card: ⚠️ Urgency and 🚨 Emergency side by side, big
//  targets (≥56px), armed state blinks on the shared atcFlash clock. All alert
//  SEMANTICS come from AlertContext (raiseUrgency/raiseEmergency/resolve/
//  acknowledge/canClear) — the existing alarm sound + vibration path lives in
//  AlertContext/audio/chime/native·urgencyAlarm and is NOT duplicated here;
//  raising the alert is enough to ring every recipient.
//
//  EMERGENCY CONFIRM PATTERN — two taps: arm, then confirm within 3s.
//  Why this and not slide/hold: a slide gesture fights the scroll view and is
//  unreliable with surgical gloves; press-and-hold has no visible affordance,
//  is invisible to screen readers, and Haptics/long-press timing varies by
//  device. Two discrete activations are the simplest thing that stops a
//  pocket-fire (one accidental touch can never send), remain fully keyboard/
//  AT-accessible (two Enter presses), and the 3s expiry fails CLOSED — a
//  stale armed button can never fire minutes later. Urgency (lower stakes,
//  private to the room's doctor) stays one tap so help is one gesture away.
//
//  Live state: any LIVE alert for THIS room renders that button armed
//  (blinking border). Tap-to-cancel resolves it when canClear allows (the
//  room's supervising doctor / AIC / admin); otherwise the button offers
//  Acknowledge (recipients), or a passive "active" status once acknowledged.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
// ─────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import { useAlertCenter } from "../../context/AlertContext";
import {
  ALERT_TYPES,
  resolveRoomDoctorId,
  resolveUrgencyTargets,
  alertSinceLabel,
  emergencyRoomStaff,
} from "../../domain/alerts";
import mobile, { px } from "../../theme/tokens.mobile";

const { color, space, radius } = mobile;

// Confirm window for the emergency second tap. Long enough for a deliberate
// double action, short enough that an accidental first tap disarms itself.
export const EMERGENCY_CONFIRM_MS = 3000;

// Ring-only blink: an alert button must stay legible WHILE it blinks. Fading
// the whole element (atcFlash) made "Emergency active — tap to cancel" half
// invisible on a real phone — the worst possible control to hide.
const FLASH = { animation: "atcRing 1s steps(1) infinite" };
const isLiveAlert = (a) => !!a && (a.status === "sent" || a.status === "acknowledged");

// showUrgency mirrors the alertsUrgency module (an admin may hide the urgency
// ping). Emergency has no such prop BY DESIGN: it is a locked module, and a
// prop to hide it here would be a second, ungoverned way to remove a
// patient-safety control that the resolver deliberately cannot.
export default function MobileAlertButtons({ room = null, assignment = null, showUrgency = true, compact = false }) {
  const { alerts, uid, raiseEmergency, raiseUrgency, resolve, acknowledge, canClear } =
    useAlertCenter();
  // Minute-resolution clock, running ONLY while an alert is live on this card.
  // A permanently ticking interval on every room card is a battery cost for a
  // number that is blank the rest of the day.
  const [now, setNow] = useState(() => new Date());
  const [confirmArmed, setConfirmArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const confirmTimer = useRef(null);

  const clearConfirm = () => {
    if (confirmTimer.current) clearTimeout(confirmTimer.current);
    confirmTimer.current = null;
    setConfirmArmed(false);
  };
  // Disarm on unmount and whenever the room changes — a pending confirm must
  // never carry over to a different room's card.
  useEffect(() => clearConfirm, [room?.id]);

  const hasLive = (alerts || []).some(
    (a) => isLiveAlert(a) && a.roomId === room?.id &&
      (a.type === ALERT_TYPES.EMERGENCY || a.type === ALERT_TYPES.URGENCY)
  );
  useEffect(() => {
    if (!hasLive) return undefined;
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, [hasLive]);

  if (!room?.id) return null;

  // The LIVE alert of each type for THIS room (any sender — the card shows the
  // room's true state, and permissions decide which control appears).
  const liveOf = (type) =>
    (alerts || []).find((a) => a.type === type && isLiveAlert(a) && a.roomId === room.id) || null;
  const liveEmergency = liveOf(ALERT_TYPES.EMERGENCY);
  const liveUrgency = liveOf(ALERT_TYPES.URGENCY);
  // Whether an Urgency control is actually on screen — the SAME condition the
  // markup below branches on, so the column count and the button count can
  // never disagree. A live urgency renders even when the module is hidden,
  // because an active alert must stay cancellable.
  const showsUrgency = Boolean(liveUrgency || showUrgency);

  // ⚠️ COMPACT IS FOR THE IDLE STATE ONLY. Owner asked for these reduced to two
  // small coloured squares at the top of Room Status, and that is right while
  // nothing is happening — the page is about the case, not about the alarm.
  //
  // But a LIVE alert, or one armed and waiting for its confirming tap, expands
  // back to full width. "Emergency active — tap to cancel" and "Tap again to
  // confirm" are instructions somebody reads under pressure, and a 40px square
  // cannot carry them. Shrinking the one control that matters at the moment it
  // starts mattering is the opposite of what a compact idle state is for.
  const isCompact = compact && !liveEmergency && !liveUrgency && !confirmArmed;

  const doResolve = async (alert, label) => {
    setBusy(true);
    try {
      await resolve(alert.id);
      setStatus(`${label} cancelled.`);
    } catch (e) {
      console.error("Mobile alert resolve failed:", e);
      setStatus(`Could not cancel the ${label.toLowerCase()}.`);
    } finally {
      setBusy(false);
    }
  };

  const doAcknowledge = async (alert, label) => {
    setBusy(true);
    try {
      await acknowledge(alert.id);
      setStatus(`${label} acknowledged.`);
    } catch (e) {
      console.error("Mobile alert acknowledge failed:", e);
      setStatus(`Could not acknowledge the ${label.toLowerCase()}.`);
    } finally {
      setBusy(false);
    }
  };

  const sendUrgency = async () => {
    const { doctorId, crnaId } = resolveUrgencyTargets(room, assignment);
    if (!doctorId) {
      setStatus(`No doctor assigned to ${room.name} yet.`);
      return;
    }
    setBusy(true);
    try {
      const r = await raiseUrgency({ roomId: room.id, roomName: room.name, doctorId, crnaId });
      setStatus(r.ok ? `⚡ Urgent ping sent to ${room.name}'s doctor.` : `No doctor assigned to ${room.name} yet.`);
    } catch (e) {
      console.error("Mobile urgency failed:", e);
      setStatus("Could not send the urgent ping.");
    } finally {
      setBusy(false);
    }
  };

  // First emergency tap: ARM only — nothing is sent. Auto-disarms after the
  // confirm window so an accidental brush can never fire later.
  const armEmergency = () => {
    setStatus(null);
    setConfirmArmed(true);
    confirmTimer.current = setTimeout(() => {
      confirmTimer.current = null;
      setConfirmArmed(false);
    }, EMERGENCY_CONFIRM_MS);
  };

  // Second tap inside the window: actually raise.
  const confirmEmergency = async () => {
    clearConfirm();
    setBusy(true);
    try {
      // Names of the people in the room travel WITH the alert, so every
      // supervisor's phone can say who they are walking towards without
      // opening a listener of its own.
      const staff = emergencyRoomStaff(room, assignment);
      const r = await raiseEmergency({
        roomId: room.id,
        roomName: room.name,
        doctorId: resolveRoomDoctorId(room, assignment),
        doctorName: staff.doctorName,
        crnaId: staff.crnaId,
        crnaName: staff.crnaName,
      });
      if (r.ok) setStatus(`🚨 Emergency for ${room.name} sent to ${r.targetCount}.`);
      else if (r.reason === "duplicate") setStatus(`An emergency is already active for ${room.name}.`);
      else if (r.reason === "no_targets") setStatus("No doctors or AIC on today's roster to alert.");
      else setStatus("Could not send the emergency. Call directly if critical.");
    } catch (e) {
      console.error("Mobile emergency failed:", e);
      setStatus("Could not send the emergency. Call directly if critical.");
    } finally {
      setBusy(false);
    }
  };

  // One armed slot: cancel when allowed, acknowledge when a recipient, passive
  // once acknowledged. `label` is "Emergency" / "Urgency" for copy + a11y.
  const renderArmed = (alert, label, emoji, styleBase) => {
    // "18:42 · 7 min" — an alert blinking for two minutes and one blinking for
    // forty are different situations, and the card could not tell them apart.
    const since = alertSinceLabel(alert, now);
    const Since = () =>
      since ? (
        <span style={styles.since}>{since}</span>
      ) : null;
    if (canClear(alert)) {
      return (
        <button
          type="button"
          disabled={busy}
          onClick={() => doResolve(alert, label)}
          aria-label={`${label} active in ${room.name}${since ? ` since ${since}` : ""} — tap to cancel`}
          style={{ ...styleBase, ...styles.armed, borderColor: color.focus.ring, ...FLASH }}
        >
          <span style={styles.emoji} aria-hidden="true">{emoji}</span>
          <span style={styles.armedText}>
            {label} active — tap to cancel
            <Since />
          </span>
        </button>
      );
    }
    const ackedByMe = Array.isArray(alert.acknowledgedBy) && alert.acknowledgedBy.includes(uid);
    if (ackedByMe) {
      return (
        <div
          role="status"
          aria-label={`${label} active in ${room.name}${since ? ` since ${since}` : ""} — acknowledged`}
          style={{ ...styleBase, ...styles.armed, cursor: "default", ...FLASH }}
        >
          <span style={styles.emoji} aria-hidden="true">{emoji}</span>
          <span style={styles.armedText}>
            {label} active — acknowledged
            <Since />
          </span>
        </div>
      );
    }
    return (
      <button
        type="button"
        disabled={busy}
        onClick={() => doAcknowledge(alert, label)}
        aria-label={`${label} active in ${room.name}${since ? ` since ${since}` : ""} — acknowledge`}
        style={{ ...styleBase, ...styles.armed, ...FLASH }}
      >
        <span style={styles.emoji} aria-hidden="true">{emoji}</span>
        <span style={styles.armedText}>
          {label} active — acknowledge
          <Since />
        </span>
      </button>
    );
  };

  return (
    <section style={styles.card} aria-label={`Alerts for ${room.name}`}>
      <h3 style={styles.heading}>Alerts</h3>
      {/* ⚠️ TWO COLUMNS ONLY WHEN THERE ARE TWO BUTTONS. A fixed "1fr 1fr"
          left Emergency stranded in the first column whenever Urgency was
          hidden — half the card empty beside the one control on this screen
          that must never be hunted for. With one button the row is one column,
          so it fills the card: centred, and a bigger target for the alert you
          press under pressure. */}
      <div style={isCompact ? styles.rowCompact : showsUrgency ? styles.grid : styles.gridSolo}>
        {/* ⚠️ URGENCY — one tap, private ping to the room's doctor. Hidden
            when the facility config turns the module off — but a LIVE urgency
            still renders (an active alert must stay cancellable/visible even
            if the send control was just hidden). */}
        {liveUrgency ? (
          renderArmed(liveUrgency, "Urgency", "⚠️", styles.urgencyBtn)
        ) : showUrgency ? (
          <button
            type="button"
            disabled={busy}
            onClick={sendUrgency}
            aria-label={`Send urgent ping for ${room.name} to its doctor`}
            style={isCompact ? { ...styles.urgencyBtn, ...styles.btnCompact } : styles.urgencyBtn}
          >
            <span style={styles.emoji} aria-hidden="true">⚠️</span>
            {!isCompact && "Urgency"}
          </button>
        ) : null}

        {/* 🚨 EMERGENCY — two taps (arm → confirm within 3s), broadcast. */}
        {liveEmergency ? (
          renderArmed(liveEmergency, "Emergency", "🚨", styles.emergencyBtn)
        ) : confirmArmed ? (
          <button
            type="button"
            disabled={busy}
            onClick={confirmEmergency}
            aria-label={`Confirm emergency for ${room.name} — expires in 3 seconds`}
            style={{ ...styles.emergencyBtn, ...styles.armed, ...FLASH }}
          >
            <span style={styles.emoji} aria-hidden="true">🚨</span>
            Tap again to confirm
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={armEmergency}
            aria-label={`Send emergency for ${room.name} — a second tap confirms`}
            style={isCompact ? { ...styles.emergencyBtn, ...styles.btnCompact } : styles.emergencyBtn}
          >
            <span style={styles.emoji} aria-hidden="true">🚨</span>
            {!isCompact && "Emergency"}
          </button>
        )}
      </div>
      {/* Outcome line — polite live region so AT hears sends/cancels/failures. */}
      {status && (
        <p role="status" style={styles.status}>
          {status}
        </p>
      )}
    </section>
  );
}

const btnBase = {
  // 48px: comfortably past the 44px touch floor with 8px+ separation from the
  // break pills above, but no longer the 56px slab that dominated the card and
  // crowded those pills. Emoji + label now sit on ONE line, which is what
  // actually removes the height — a stacked column forced the button to be
  // twice as tall as its content needed.
  minHeight: 48,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  gap: px(6),
  padding: `${px(space.xs)} ${px(space.sm)}`,
  borderRadius: radius.md,
  fontSize: 13,
  fontWeight: 800,
  fontFamily: "inherit",
  cursor: "pointer",
  textAlign: "center",
  lineHeight: 1.15,
  width: "100%",
};

const styles = {
  card: {
    background: color.surface.cardGradient,
    border: `1px solid ${color.border.card}`,
    borderRadius: radius.md,
    padding: px(space.sm),
    // Breathing room from the break pills directly above — they were nearly
    // touching the alert block on a real phone.
    marginTop: px(space.md),
  },
  heading: {
    margin: `0 0 ${px(space.sm)} 0`,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: color.text.muted,
  },
  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: px(space.sm) },
  gridSolo: { display: "grid", gridTemplateColumns: "1fr", gap: px(space.sm) },
  // Two squares, right-aligned, out of the way until they are needed.
  rowCompact: { display: "flex", gap: px(space.sm), justifyContent: "flex-end" },
  // ⚠️ 44px, NOT the 24px a dense board control may use. This is the emergency
  // control on a phone held by somebody with gloves on: it keeps a full
  // touch-target (WCAG 2.5.5 enhanced) even while it is visually small, and it
  // keeps its own colour so it is found by hue, not by reading.
  btnCompact: {
    width: px(44),
    minWidth: px(44),
    height: px(44),
    minHeight: px(44),
    padding: 0,
    gap: 0,
    fontSize: 0,
    flex: "0 0 auto",
  },
  emoji: { fontSize: 16, lineHeight: 1, flexShrink: 0 },
  // giving amber family — glyphInk on it measures 8.69 (tokens audit).
  // Longhand border props so the armed borderColor override never mixes
  // shorthand + longhand (React rerender styling warning).
  urgencyBtn: {
    ...btnBase,
    background: color.break.giving,
    borderWidth: 1.5,
    borderStyle: "solid",
    borderColor: color.break.giving,
    color: color.break.glyphInk,
  },
  // requested red family — glyphInk on it measures 4.96 (tokens audit).
  emergencyBtn: {
    ...btnBase,
    background: color.break.requested,
    borderWidth: 1.5,
    borderStyle: "solid",
    borderColor: color.break.requested,
    color: color.break.glyphInk,
  },
  // Armed = blinking border on the shared atcFlash clock (suppressed under
  // prefers-reduced-motion globally, like every web blink).
  armed: { outline: `2px solid ${color.focus.ring}`, outlineOffset: 1 },
  // The armed label becomes two stacked lines, so it needs its own column.
  armedText: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: px(1),
    minWidth: 0,
  },
  // Same ink as the label, one step down — the time is context, not the point.
  since: { fontSize: 10, fontWeight: 700, opacity: 0.85 },
  status: {
    margin: `${px(space.sm)} 0 0 0`,
    fontSize: 12,
    fontWeight: 600,
    color: color.text.primary,
  },
};
